<template>
    <v-layout row>
        <v-flex xs12>

            <!-- HEADER -->
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">

                    <!-- back -->
                    <v-btn dark icon title="Back" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>

                    <v-spacer></v-spacer>

                    <!-- avatar edit -->
                    <v-btn v-if="!loading && isMyProfile" dark icon title="Change avatar" @click="dialogEditAvatar=true">
                        <v-icon>photo_camera</v-icon>
                    </v-btn>

                    <!-- help -->
                    <help-link v-if="!loading && !isMyProfile" appId="company" pageId="user-profile" color="white"></help-link>

                    <!-- 201 files -->
                    <v-chip v-if="canView201Files && !loading" @click="view201Files" label outline small title="Click to view 201 files">
                        <v-icon color="#F9A825">folder</v-icon>
                        <span class="header-files white--text ml-5">
                            View 201 Files
                        </span>
                    </v-chip>

                    <!-- deactivate -->
                    <v-btn v-if="!loading && canDeactivate" @click="showDeactivateConfirmation" title="Deactivate account" small class="no-uppercase">
                        <v-icon color="red">person_off</v-icon>
                        <span class="mr-3">Deactivate</span>
                    </v-btn>

                    <!-- deactivated -->
                    <v-chip v-if="!loading && !isActiveAccount && !isMobile" outline label small>
                        <v-icon color="grey">person_off</v-icon>
                        <span class="grey--text ml-5">
                            Deactivated on {{deactivatedOn}}
                        </span>
                    </v-chip>

                </v-toolbar>
            </v-card>

            <!-- ERROR -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-8">
                Error encountered. Please contact support@sweldonow.ph. Error Details: {{errorMessage}}
            </v-alert>

            <!-- PERSONAL DETAILS -->
            <v-card v-if="!loading" class="elevation-1 mt-5">
                <v-toolbar @click="employeeSectionVisible=!employeeSectionVisible" class="clickable" flat height="45">
                    <span class="font-weight-bold primary--text">
                        PERSONAL DETAILS
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="canEditPersonal && employeeSectionVisible"
                        icon
                        title="Edit Personal Details"
                        @click.stop
                        @click="showEditMember">
                        <v-icon small>edit</v-icon>
                    </v-btn>

                    <v-btn icon>
                        <v-icon>{{ employeeSectionVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>

                </v-toolbar>

                <v-card-text v-if="employeeSectionVisible">
                    <personal-details 
                        ref="employeeSectionRef" 
                        :is_myprofle="isMyProfile" 
                        :basic_info="basic_info" 
                        :address_info="address_info"
                        @changeAvatar="dialogEditAvatar=true">
                    </personal-details>
                </v-card-text>
            </v-card>

            <!-- TEAM MEMBERSHIP -->
            <v-card v-if="!loading" class="elevation-1 mt-5">
                <v-toolbar
                    @click="teamSectionVisible=!teamSectionVisible"
                    class="clickable"
                    flat
                    height="45">

                    <span class="font-weight-bold primary--text">
                        TEAM MEMBERSHIPS
                    </span>

                    <v-spacer></v-spacer>

                    <v-btn
                        v-if="canManageTeams && teamSectionVisible"
                        icon
                        title="Add Team Membership"
                        @click.stop
                        @click="showAddTeamMembership">

                        <v-icon>add</v-icon>
                    </v-btn>

                    <v-btn icon>
                        <v-icon>
                            {{ teamSectionVisible ? 'expand_less' : 'expand_more' }}
                        </v-icon>
                    </v-btn>

                </v-toolbar>

                <v-data-table
                    v-if="teamSectionVisible"
                    :headers="isMobile ? [] : headers"
                    :items="team_membership"
                    :loading="loading"
                    :hide-headers="isMobile">

                    <template v-slot:items="props">

                        <!-- MOBILE -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">

                                <v-card class="elevation-1">

                                    <v-toolbar flat height="42">

                                        <v-chip small class="user-chip-container">
                                            {{ props.item.team }}
                                        </v-chip>

                                        <v-spacer></v-spacer>

                                        <v-menu
                                            v-if="canManageTeams"
                                            left
                                            offset-y>

                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn
                                                    icon
                                                    small
                                                    v-bind="attrs"
                                                    v-on="on">
                                                    <v-icon small>more_vert</v-icon>
                                                </v-btn>
                                            </template>

                                            <v-list dense>

                                                <v-list-tile
                                                    @click="editAssignment(props.item)">
                                                    <v-list-tile-title>
                                                        Edit
                                                    </v-list-tile-title>
                                                </v-list-tile>

                                                <v-list-tile
                                                    v-if="canDeleteTeamMembership"
                                                    @click="showDelete(props.item)">
                                                    <v-list-tile-title>
                                                        Delete
                                                    </v-list-tile-title>
                                                </v-list-tile>

                                            </v-list>

                                        </v-menu>

                                    </v-toolbar>

                                    <v-layout
                                        row
                                        wrap
                                        align-center
                                        :class="isMobile ? 'section-layout-mobile' : 'section-layout'">

                                        <v-flex xs12 class="text-xs-left mt-5">

                                            <v-layout row>

                                                <v-flex xs5>
                                                    <div class="caption grey--text">
                                                        Role
                                                    </div>
                                                    <div>{{ props.item.role }}</div>
                                                </v-flex>

                                                <v-flex xs7>
                                                    <div class="caption grey--text">
                                                        Supervisor
                                                    </div>

                                                    <v-chip
                                                        v-if="props.item.supervisor && props.item.supervisor.userId"
                                                        class="user-chip-container"
                                                        small>

                                                        <v-avatar>
                                                            <v-img
                                                                :src="awsS3Helper.getAvatarUrl(props.item.supervisor.userId)">
                                                            </v-img>
                                                        </v-avatar>

                                                        {{props.item.supervisor.firstName}}
                                                        {{props.item.supervisor.lastName}}

                                                    </v-chip>

                                                </v-flex>

                                            </v-layout>

                                        </v-flex>

                                    </v-layout>

                                </v-card>

                            </td>
                        </tr>

                        <!-- DESKTOP -->
                        <tr
                            v-else
                            @click="canManageTeams ? editAssignment(props.item) : null"
                            :style="canManageTeams ? 'cursor:pointer' : ''">

                            <td>{{ props.item.team }}</td>

                            <td>{{ props.item.role }}</td>

                            <td>

                                <v-chip
                                    v-if="props.item.supervisor && props.item.supervisor.userId"
                                    class="user-chip-container"
                                    small>

                                    <v-avatar>
                                        <v-img
                                            :src="awsS3Helper.getAvatarUrl(props.item.supervisor.userId)">
                                        </v-img>
                                    </v-avatar>

                                    {{props.item.supervisor.firstName}}
                                    {{props.item.supervisor.lastName}}

                                </v-chip>

                            </td>

                            <td>
                                {{ props.item.visibleInMyTeams ? 'Yes' : 'No' }}
                            </td>

                            <td @click.stop>

                                <v-btn
                                    v-if="canManageTeams && canDeleteTeamMembership"
                                    icon
                                    title="Delete"
                                    @click="showDelete(props.item)">

                                    <v-icon small>delete</v-icon>

                                </v-btn>

                            </td>

                        </tr>

                    </template>

                </v-data-table>

            </v-card>

            <!-- EMPLOYMENT -->
            <v-card v-if="!loading" class="elevation-1 mt-5">
                <v-toolbar @click="employmentSectionVisible=!employmentSectionVisible" class="clickable" flat height="45">
                    <span class="font-weight-bold primary--text">
                        EMPLOYMENT
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn
                        v-if="canEditEmployment && employmentSectionVisible"
                        icon
                        title="Edit Employment"
                        @click.stop
                        @click="showEditEmployment">

                        <v-icon small>edit</v-icon>
                    </v-btn>

                    <v-btn icon>
                        <v-icon>
                            {{ employmentSectionVisible ? 'expand_less' : 'expand_more' }}
                        </v-icon>
                    </v-btn>

                </v-toolbar>

                <v-card-text v-if="employmentSectionVisible">

                    <employment
                        :employment_info="employment_info">
                    </employment>

                </v-card-text>

            </v-card>

            <!-- WORKDAYS -->
            <v-card v-if="!loading" class="elevation-1 mt-5">
                <v-toolbar @click="workdaysSectionVisible=!workdaysSectionVisible" class="clickable" flat height="45">
                    <span class="font-weight-bold primary--text">
                        WORKDAYS
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn icon>
                        <v-icon>{{ workdaysSectionVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text v-if="workdaysSectionVisible">
                    <v-layout row wrap align-center :class="isMobile ? 'ml-20' : 'ml-100'">
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Sunday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.sunday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.sunday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Sunday', workdays.sunday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Monday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.monday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.monday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Monday', workdays.monday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Tuesday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.tuesday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.tuesday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Tuesday', workdays.tuesday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Wednesday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.wednesday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.wednesday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Wednesday', workdays.wednesday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Thursday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.thursday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.thursday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Thursday', workdays.thursday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Friday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.friday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.friday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Friday', workdays.friday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Saturday</div>
                            <div v-if="isMyProfile">{{ getWorkdayDescription(workdays.saturday) }}</div>
                            <v-switch 
                                v-else
                                v-model="workdays.saturday" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getWorkDayTooltip('Saturday', workdays.saturday)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Attendance Required</div>
                            <div v-if="isMyProfile">
                                <span v-if="workdays.clockInOutRequired">Yes</span>
                                <span v-else>No</span>
                            </div>
                            <v-switch 
                                v-else
                                v-model="workdays.clockInOutRequired" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getAttendanceRequiredTooltip(workdays)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Attendance Pic Required</div>
                            <div v-if="isMyProfile">
                                <span v-if="workdays.clockInOutPicRequired">Yes</span>
                                <span v-else>No</span>
                            </div>
                            <v-switch 
                                v-else
                                v-model="workdays.clockInOutPicRequired" 
                                :readonly="!hasEditRights" 
                                color="primary" 
                                @change="updateWorkday(workdays)" 
                                :title="getclockInOutPicRequiredTooltip(workdays)" 
                                class="mt-0">
                            </v-switch>
                        </v-flex>
                        <v-flex xs4 sm2>
                            <div class="caption grey--text">Work Hours Per Day</div>
                            <div v-if="isMyProfile">
                                <span>{{workdays.workHoursPerDay}}</span>
                            </div>
                            <v-select
                                v-else
                                :items="[7,8,9]"
                                v-model="workdays.workHoursPerDay"     
                                @change="updateWorkday(workdays)"   
                                class="mt-0 work-hours" 
                                title="Click to set working hours per day"
                            ></v-select>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- BANK -->
            <v-card v-if="!loading" class="elevation-1 mt-5">
                <v-toolbar @click="bankSectionVisible=!bankSectionVisible" class="clickable" flat height="45">
                    <span class="font-weight-bold primary--text">
                        BANK DETAILS
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="canEditBankDetails && bankSectionVisible" icon title="Edit Bank" @click.stop @click="showEditBankDetails">
                        <v-icon small>edit</v-icon>
                    </v-btn>
                    <v-btn icon>
                        <v-icon>{{ bankSectionVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text v-if="bankSectionVisible">
                    <v-layout row wrap :class="isMobile ? 'ml-20' : 'ml-100'">
                        <v-flex xs12>
                            <div class="caption grey--text">Bank</div>
                            <div>{{bank_account ? bank_account.bankName : 'Not Updated'}}</div>
                        </v-flex>
                        <v-flex xs12>
                            <div class="caption grey--text">Branch</div>
                            <div>{{bank_account ? bank_account.bankBranch : 'Not Updated'}}</div>
                        </v-flex>
                        <v-flex xs12>
                            <div class="caption grey--text">Bank Account</div>
                            <div>{{bank_account ? bank_account.bankAccount : 'Not Updated'}}</div>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- COMMENTS + HISTORY -->
            <v-layout row wrap class="mt-5">

                <v-flex xs12 sm6>
                    <comments-history
                        v-if="!loading"
                        :comments_data="commentsData">
                    </comments-history>
                </v-flex>

                <v-flex xs12 sm6>
                    <update-history
                        v-if="!loading"
                        ref="updateHistoryRef"
                        :page_query_id="selectedUserId">
                    </update-history>
                </v-flex>

            </v-layout>

        </v-flex>

        <!-- DIALOGS -->
        <v-dialog v-model="dialogUpdate" persistent width="700">
            <update-member ref="updateMemberDialog" :dialogUpdate.sync="dialogUpdate" @send="getProfile(false)"></update-member>
        </v-dialog>
        <v-dialog v-model="dialogEmployment" persistent width="650">
            <update-employment ref="refEmployment" :dialogEmployment.sync="dialogEmployment" @send="getProfile(false)"></update-employment>
        </v-dialog>
        <v-dialog v-model="dialogUpdateBank" persistent width="650">
            <update-bank ref="refBank" :dialogUpdateBank.sync="dialogUpdateBank" @send="getProfile(false)"></update-bank>
        </v-dialog>
        <v-dialog v-model="dialogUpdateTeamMembership" persistent width="750">
            <team-membership ref="refUpdateTeamMembership" :dialogUpdateTeamMembership.sync="dialogUpdateTeamMembership" @send="getProfile(false)"></team-membership>
        </v-dialog>
        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" :dialogDelete.sync="dialogDelete" @okDelete="deleteTeamAssignment"></delete>
        </v-dialog>
        <v-dialog v-model="dialogOkCancel" persistent width="600">
            <ok-cancel :msg="dialogOkCancelMsg" alignLeft="true" title="Deactivate Employee Account" :dialogOkCancel.sync="dialogOkCancel" @ok="deactiveUser"></ok-cancel>
        </v-dialog>
        <v-dialog v-model="dialogEditAvatar" fullscreen hide-overlay transition="dialog-bottom-transition">
            <edit-avatar :dialogEditAvatar.sync="dialogEditAvatar" @send="refreshAvatar"></edit-avatar>
        </v-dialog>

        <!-- STATUS -->
        <status-messaging :page_query_id="selectedUserId" @refresh="updateHandler"></status-messaging>
    </v-layout>
</template>

<script>

import commonfn from '@/common/functions'   //for refactor
import employeeApi from '@/api/employees'
import userApi from '@/api/user'
import teamApi from '@/api/team'
import workdaysApi from '@/api/workdays'

import pageKeys from '@/common/pagekeys'
import notificationTypes from '@/common/notificationTypes'

import awsS3Helper from '@/helpers/aws-s3'

import {
    hasAddRights,
    hasEditRights
} from '@/helpers/access-permissions'

import {
    getExpandCollapseVisibility,
    updateExpandCollapseVisibility
} from '@/helpers/user-prefs'

export default {
    name: 'UserProfile',
    metaInfo: {
        title: 'User Profile',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View and update Personal Details, Team Membership, Employment, Bank and Workdays' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'personal-details': () => import('@/components/UserProfile/PersonalDetails'),
        'employment': () => import('@/components/UserProfile/Employment'),
        'team-membership': () => import("@/components/ManageTeam/UpdateTeamMembership"),
        'update-employment': () => import("@/components/ManageTeam/EditEmployment"),
        'update-member': () => import("@/components/ManageTeam/UpdateMember"),
        'update-bank': () => import("@/components/ManageTeam/UpdateBankDetails"),
        'edit-avatar': () => import("@/components/Users/EditAvatar"),
        'delete': () => import('@/components/BasicPopups/Delete'),
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
        'comments-history': () => import('@/components/Common/CommentsHistory'),
    },
    data () {
        const routeName = this.$route.name

        return {

            commonfn,
            awsS3Helper,

            employeeSectionVisible: getExpandCollapseVisibility(routeName, 'employee-details-appearance'),
            teamSectionVisible: getExpandCollapseVisibility(routeName, 'team-details-appearance'),
            employmentSectionVisible: getExpandCollapseVisibility(routeName, 'employment-details-appearance'),
            workdaysSectionVisible: getExpandCollapseVisibility(routeName, 'workdays-details-appearance'),
            bankSectionVisible: getExpandCollapseVisibility(routeName, 'bank-details-appearance'),
            
            loading: false,
            error: false,
            errorMessage: '',

            dialogDelete: false,
            dialogOkCancel: false,
            dialogOkCancelMsg: null,

            dialogUpdate: false,
            dialogUpdateTeamMembership: false,
            dialogEmployment: false,
            dialogUpdateBank: false,
            dialogEditAvatar: false,

            headers: [
                {text:'Team', value:'team', sortable:false},
                {text:'Role', value:'role', sortable:false},
                {text:'Supervisor', value:'supervisor', sortable:false},
                {text:'Visible', value:'visibleInMyTeams', sortable:false},
                {text:'', value:'actions', sortable:false},
            ],

            basic_info: null,
            address_info: null,
            employment_info: null,
            team_membership: [],
            bank_account: null,
            workdays: null,

            teamMemberId: null
        }
    },    
    methods: {
        async getProfile(show_loading) {
            try {

                this.loading = show_loading

                const response = this.isMyProfile
                    ? await userApi.getMyProfile()
                    : await employeeApi.get(this.selectedUserId)

                if (response && response.status == 200) {

                    this.basic_info = response.data.basicInfo
                    this.address_info = response.data.address
                    this.employment_info = response.data.employment
                    this.team_membership = response.data.teamMembership
                    this.bank_account = response.data.bankAccount
                    this.workdays = response.data.workdays
                }

                this.loading = false

            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        getWorkdayDescription(workday) {
            return workday ? 'Working' : 'Rest'
        },
        async updateWorkday(data) {
            try {
                await workdaysApi.update(data)
            } catch (error) {
                this.errorMessage = error
                this.error = true
            }
        },
        getWorkDayTooltip(day, isWorking) {
            return isWorking
            ? `Click to mark ${day} as a non-working day`
            : `Click to mark ${day} as a working day`;
        },
        getAttendanceRequiredTooltip(data) {
            return data.clockInOutRequired ?
                'Click to make clock-in/out NOT required' :
                'Click to make clock-in/out required'
        },
        getclockInOutPicRequiredTooltip(data) {
            return data.clockInOutPicRequired ?
                'Click to make clock-in/out picture NOT required' :
                'Click to make clock-in/out picture required'
        },
        refreshAvatar() {
            this.$refs.employeeSectionRef.refreshAvatar()
        },
        view201Files() {
            this.$router.push(`/hr/201/${this.selectedUserId}/files`)
        },
        showEditMember() {
            this.$refs.updateMemberDialog.setParamsEdit(
                this.basic_info,
                this.address_info
            )

            this.dialogUpdate = true
        },
        showEditEmployment() {
            this.$refs.refEmployment.setParams(
                !this.isMyProfile,
                this.basic_info,
                this.employment_info
            )

            this.dialogEmployment = true
        },
        showEditBankDetails() {
            this.$refs.refBank.setParams({
                userId: this.selectedUserId,
                bankDetails: this.bank_account
            })

            this.dialogUpdateBank = true
        },
        showAddTeamMembership() {
            this.$refs.refUpdateTeamMembership.setParams({
                mode: 'Add',
                userId: this.selectedUserId,
                membership: this.team_membership
            })

            this.dialogUpdateTeamMembership = true
        },
        editAssignment(editData) {
            if (!this.canManageTeams) return

            this.$refs.refUpdateTeamMembership.setParams({
                mode: 'Update',
                userId: this.selectedUserId,
                roleId: editData.roleId,
                teamId: editData.teamId,
                supervisorId: editData.supervisor.userId,
                visibleInMyTeams: editData.visibleInMyTeams
            })

            this.dialogUpdateTeamMembership = true
        },
        showDelete(data) {
            this.teamMemberId = data.teamMemberId

            this.$refs.refDeleteComponent.setMessage(
                "Delete this team assignment?"
            )

            this.dialogDelete = true
        },
        async deleteTeamAssignment() {
            try {

                await teamApi.deleteMembership(this.teamMemberId)
                this.dialogDelete = false
                await this.getProfile(false)

            } catch(error) {
                this.error = true
                this.errorMessage = error
            }
        },
        showDeactivateConfirmation() {
            this.dialogOkCancelMsg =
                `Deactivating this account will:<br><br>
                <ul>
                    <li>Make employee details and HR 201 files read-only</li>
                    <li>Disable payroll and annualization</li>
                    <li>Exclude employee from billing</li>
                    <li>Prevent future reactivation</li>
                </ul>
                <br>
                <b>Proceed?</b>`

            this.dialogOkCancel = true
        },
        async deactiveUser() {

            try {

                await userApi.deactivate(this.selectedUserId)

                await this.getProfile(true)

                this.dialogOkCancel = false

            } catch(error) {

                this.error = true
                this.errorMessage = error
            }
        },
        updateHandler() {
            this.$refs.updateHistoryRef.getHistory()
        },
        updateHeader() {
            this.$root.$emit('setHeaderTitles', {
                display: true,
                headerTitle: '',
                headerPageTitle: this.isMyProfile
                    ? 'My Profile'
                    : `${this.basic_info.firstName} ${this.basic_info.lastName}`,
                avatarPath: this.isMyProfile
                    ? this.myProfileAvatar
                    : null
            })
        }
    },
    async created() {
        await this.getProfile(true)
        this.updateHeader()
    },
    watch: {
        // Watch handles all subsequent changes
        '$route'(to, from) {
            // Check if we are still on a profile-related page to avoid
            // firing the API when navigating AWAY to a completely different page
            const profileRoutes = ['MyProfile', 'UserProfile'];
            if (profileRoutes.includes(to.name)) {
                this.getProfile(true);
                this.updateHeader();
            }
        },
        employeeSectionVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'employee-details-appearance',newVal)
        },
        teamSectionVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'team-details-appearance', newVal)
        },
        employmentSectionVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'employment-details-appearance', newVal)
        },
        workdaysSectionVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'workdays-details-appearance', newVal)
        },
        bankSectionVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'bank-details-appearance', newVal)
        }
    },
    computed: {
        isMyProfile() {
            return this.$route.name === 'MyProfile'
        },
        selectedUserId() {
            if (this.isMyProfile)
                return this.$store.state.userId

            return this.$route.params.userId
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
        canEditPersonal() {
            return this.isActiveAccount &&
                (this.isMyProfile || this.hasEditRights)
        },
        canEditEmployment() {
            return this.isActiveAccount &&
                (this.isMyProfile || this.hasEditRights)
        },
        canEditBankDetails() {
            if (!this.isActiveAccount)
                return false

            // as of this writing, updating bank details is turned off in MyProfile
            // to avoid changes when payroll team is already doing bank transfer
            if (this.isMyProfile)
                return false

            return this.bank_account
                ? this.hasEditRights
                : this.hasAddRights
        },
        canManageTeams() {
            return !this.isMyProfile &&
                this.hasEditRights &&
                this.isActiveAccount
        },
        canView201Files() {
            return !this.isMyProfile
        },
        canDeactivate() {
            return !this.isMyProfile &&
                this.isResignedOrTerminated &&
                this.hasEditRights &&
                this.isActiveAccount
        },
        canDeleteTeamMembership() {
            return this.team_membership.length > 1
        },
        commentsData() {
            return {
                pageQueryId: this.selectedUserId,
                pageItemCreatedBy: null,
                pageLink: this.$router.currentRoute.path,
                pageKey: pageKeys.employees,
                notificationTypeId: notificationTypes.myprofile
            }
        },
        isResignedOrTerminated() {
            return this.employment_info &&
                (
                    this.employment_info.employmentStatus == commonfn.getStatusCode('Resigned') ||
                    this.employment_info.employmentStatus == commonfn.getStatusCode('Terminated')
                )
        },
        isActiveAccount() {
            return this.basic_info && !this.basic_info.deactivated
        },
        deactivatedOn() {

            if (this.basic_info && this.basic_info.deactivatedOn) {
                return this.$moment(this.basic_info.deactivatedOn)
                    .format('MMM DD, YYYY')
            }
        },
        myProfileAvatar() {
            return this.$store.state.avatar
        }
    }
}
</script>

<style scoped>
.section-layout {
    padding:0px 20px 0px 50px;
}
.section-layout-mobile {
    padding:0px 10px 0px 20px;
}
.header-files {
    cursor:pointer;
}
.header-files:hover {
    text-decoration:underline;
}
.work-hours {
    width:60px;
}
</style>